import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Collapse,
  Tooltip,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Typography,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SettingsIcon from '@mui/icons-material/Settings';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import WebIcon from '@mui/icons-material/Web';
import ArticleIcon from '@mui/icons-material/Article';
import MessageIcon from '@mui/icons-material/Message';
import LinkIcon from '@mui/icons-material/Link';
import StoreIcon from '@mui/icons-material/Store';
import AssessmentIcon from '@mui/icons-material/Assessment';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HubIcon from '@mui/icons-material/Hub';
import QuizIcon from '@mui/icons-material/Quiz';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import LoginIcon from '@mui/icons-material/Login';
import LockIcon from '@mui/icons-material/Lock';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { ConstantActions } from '../../Services/_actions/constant.actions';
import { globalpermissions } from '../../Services/Permission';
import { useKnowledgeMapSubjects } from './ConceptBuilderSideBar.tsx';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states';
import MachadaloLogoTop from '../Header/header-machadalo-logo.png';
import TestLogo from '../Header/synergytop-logo.webp';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';


const Logo = process.env.REACT_APP_NODE_ENV === 'test' ? TestLogo : MachadaloLogoTop;

const drawerWidth = 260;

const ModernSidebar = () => {
  const isNotProduction = process.env.REACT_APP_NODE_ENV !== 'production';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const itemRefs = React.useRef({});
  const [expandMap, setExpandMap] = useState<Record<string, boolean>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logo, setLogo] = useState(Logo);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery('(max-width:1024px)');
  const navigate = useNavigate();
  const location = useLocation();
  const actions = ConstantActions();
  const userInfo = useRecoilValue(userInformationAtom);
  const isSuperuser = userInfo?.is_superuser === true || userInfo?.profile?.name === 'Admin';
  const [subjectFetchTriggered, setSubjectFetchTriggered] = useState(false);
  const {
    loading: subjectsLoading,
    subjects
  } = useKnowledgeMapSubjects(subjectFetchTriggered);

  const subjectMenuItems = subjects.map((sub) => ({
    label: sub.label,
    key: sub.value,
    route: `/r/student/concept-builder?subject=${encodeURIComponent(
      sub.value
    )}`,
  }));

  const settingDrop = [
    {
      label: 'My Profile',
      value: '/r/management/profile',
      isVisible: actions?.userHasPermission([]),
      icon: <PersonIcon fontSize="small" />,
    },
    {
      label: 'Activity Logs',
      value: '/r/activity-logs',
      isVisible: actions?.userHasPermission([globalpermissions?.navBar?.menuActivityLogs]),
      icon: <HistoryIcon fontSize="small" />,
    },
    {
      label: 'View Logs',
      value: '/r/login-logs',
      isVisible: actions?.userHasPermission([globalpermissions?.navBar?.menuLoginLogs]),
      icon: <LoginIcon fontSize="small" />,
    },
    {
      label: 'Change Password',
      value: '/r/management/change-password',
      isVisible: actions?.userHasPermission([globalpermissions?.navBar?.menuChangePassword]),
      icon: <LockIcon fontSize="small" />,
    },
    {
      label: 'Account',
      value: '/r/management/account/edit',
      isVisible: actions?.userHasPermission([globalpermissions?.navBar?.menuProfileAccount]),
      icon: <AccountCircleIcon fontSize="small" />,
    },
    {
      label: 'Logout',
      value: '/#/logout',
      isVisible: true,
      icon: <LogoutIcon fontSize="small" />,
    },
  ];

  useEffect(() => {
    try {
      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        const localLogo = parsedUserInfo?.profile?.organisation?.logo_url;
        setLogo(localLogo || Logo);
      } else {
        setLogo(Logo);
      }
    } catch (err) {
      console.error('Failed to parse userInfo from localStorage', err);
      setLogo(Logo);
    }
  }, []);

  const handleToggle = () => setDrawerOpen((prev) => !prev);

  const toggleExpand = (key: string) => {
    if (key === 'conceptBuilder' && !subjectFetchTriggered) {
      setSubjectFetchTriggered(true);
    }
    setExpandMap((prev) => ({ ...prev, [key]: !prev[key] }));
    if (!drawerOpen) setDrawerOpen(true);
  };

  const isActive = (route = '') => {
    if (!route) return false;
    return location.pathname === route || location.pathname.startsWith(route);
  };

  const canSeeItem = (item: any): boolean => {
    if (!item) return false;
    const allowed = !item.permission || actions.userHasPermission(item.permission);
    if (!allowed) return false;
    if (item.route) return true;
    if (item.children && item.children.length) return item.children.some(canSeeItem);
    return false;
  };

  const hasVisibleSection = (section: { items: any[] }): boolean =>
    section?.items?.some(canSeeItem);

  const hasVisibleItems = (items) => {
    return items?.some((item) => {
      if (!item) return false;
      if (!item.permission || actions.userHasPermission(item.permission)) return true;
      if (item.children) return hasVisibleItems(item.children);
      return false;
    });
  };

  const handleNavigate = (route) => {
    setExpandMap({});
    if (route && route.startsWith('/#/')) {
      window.location.href = route;
    } else if (route) {
      navigate(route);
    }
    if (isSmallScreen) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value: string) => {
    handleMenuClose();
    if (value === '/#/logout') {
      actions?.logout();
    } else {
      handleNavigate(value);
    }
  };

  useEffect(() => {
    if (isMobile || isSmallScreen) {
      setDrawerOpen(false);
    }
  }, [location.pathname, isMobile, isSmallScreen]);

  useEffect(() => {
    if (!drawerOpen) {
      setExpandMap({});
    }
  }, [drawerOpen]);

  useEffect(() => {
    if (isSmallScreen) {
      setDrawerOpen(false);
    }
  }, [isSmallScreen]);

  const isAnyChildRouteActive = (children) => {
    for (let child of children || []) {
      if (child.route && isActive(child.route)) return true;
      if (child.children && isAnyChildRouteActive(child.children)) return true;
    }
    return false;
  };

  const menuConfig = [
    { type: 'header', title: 'Dashboard' },
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      key: 'dashboard',
      permission: [
        globalpermissions?.pagePermissions?.businessDashboard,
        globalpermissions?.navBar?.menuOpsDashBoard,
      ],
      children: [
        ...(isNotProduction
          ? [
            {
              label: 'Business',
              route: '/#/dashboard',
              permission: [globalpermissions?.pagePermissions?.businessDashboard],
            },
            {
              label: 'Business MCA',
              route: '/#/dashboard_Business',
              permission: [globalpermissions?.pagePermissions?.businessDashboard],
            },
            {
              label: 'B2B Client Dashboard',
              route: '/#/b2b/dashboard',
              permission: [globalpermissions?.pagePermissions?.businessDashboard],
            },
          ]
          : []),
        {
          label: 'Operational',
          key: 'operational',
          children: [
            {
              label: 'Entity Report',
              route: '/r/operations-dashboard/entity',
              permission: [globalpermissions?.pagePermissions?.entityReportPageAccess],
            },
            {
              label: 'User Performance',
              route: '/r/operations-dashboard/user',
              permission: [globalpermissions?.pagePermissions?.userPerformancePageAccess],
            },
          ],
        },
      ],
    },
    { type: 'header', title: 'Business Service Planning' },
    {
      title: 'Business Service Planning',
      icon: <CampaignIcon />,
      key: 'campaignPlanning',
      permission: [
        globalpermissions?.navBar?.menuCampaignList,
        globalpermissions?.pagePermissions?.campaignListPageAccess,
      ],
      children: [
        {
          label: 'Static',
          key: 'static',
          children: [
            {
              label: 'Business Service Decision Board',
              route: '/r/campaign/campaignDecisionBoard',
              permission: [globalpermissions?.pagePermissions?.opsDashBoardPage],
            },
            {
              label: 'List Of Business Service',
              route: '/r/campaign/list',
              permission: [globalpermissions?.pagePermissions?.campaignListPageAccess],
            },
            ...(isNotProduction
              ? [
                { label: 'Booking Plan', route: '/#/HDFHDF0789/releasePlan' },
                {
                  label: 'Suspense Lead Sheet',
                  route: '/#/SuspenseLeadSheet',
                  permission: [globalpermissions?.pagePermissions?.suspenseLeadSheetPageAccess],
                },
              ]
              : []),
          ],
        },
        {
          label: 'Dynamic',
          key: 'dynamic',
          children: [
            {
              label: 'Supplier',
              key: 'supplier',
              children: [
                { label: 'Standard Templates', route: '/r/supplier/base-type/list' },
                { label: 'Manage Supplier Type', route: '/r/supplier/type/list' },
                { label: 'Manage Supplier', route: '/r/supplier/list' },
              ],
            },
            {
              label: 'Inventory',
              key: 'inventory',
              children: [
                { label: 'Standard Template', route: '/r/inventory/base/list' },
                { label: 'Manage Inventory', route: '/r/inventory/list' },
              ],
            },
            {
              label: 'Booking Engine',
              key: 'bookingEngine',
              children: [
                { label: 'Standard Templates', route: '/r/booking/base/list' },
                { label: 'Booking Templates', route: '/r/booking/template/list' },
                { label: 'Manage Bookings', route: '/r/booking/campaigns' },
              ],
            },
            ...(isNotProduction
              ? [
                {
                  label: 'Business Service Release',
                  key: 'campaignRelease',
                  children: [
                    { label: 'Release and Audit Plan', route: '/#/HDFHDF0789/auditReleasePlan' },
                    { label: 'Manage Phases', route: '/#/HDFHDF0789/managePhases' },
                  ],
                },
              ]
              : []),
          ],
        },
      ],
    },
    { type: 'header', title: 'Business Service Execution' },
    {
      title: 'Business Service Execution',
      icon: <ChecklistIcon />,
      key: 'campaignExecution',
      permission: [
        globalpermissions?.navBar?.menuCampaignLeads,
        globalpermissions?.navBar?.menuChecklist,
      ],
      children: [
        {
          label: 'Business Service Checklist',
          route: '/r/checklist/campaigns',
          permission: [globalpermissions?.navBar?.menuChecklist],
        },
      ],
    },
    {
      title: 'Records',
      icon: <HowToRegIcon />,
      key: 'leads',
      route: '/r/records/v1',
      permission: [
        globalpermissions?.navBar?.menuCampaignLeads,
        globalpermissions?.pagePermissions?.campaignLeadsPageAccess,
      ],
    },
    { type: 'header', title: 'Reporting' },
    {
      title: 'Reporting',
      icon: <AssessmentIcon />,
      key: 'reporting',
      permission: [globalpermissions?.reports?.viewReport],
      route: '/r/records/view-report',
    },
    { type: 'header', title: 'Management' },
    {
      title: 'Management',
      icon: <PeopleIcon />,
      key: 'management',
      permission: [globalpermissions?.navBar?.viewManagement],
      children: [
        {
          label: 'Organization',
          key: 'organization',
          children: [
            { label: 'View', route: '/r/management/organisation-list' },
            { label: 'On Boarding', route: '/r/management/organisation-onboard' },
            { label: 'Organizations Mapping', route: '/r/management/organisation-mapping' },
            { label: 'Update Address', route: '/r/management/update-address' },
            { label: 'Update GST', route: '/r/management/update-gst' },
          ],
        },
        { label: 'Manage Profile', route: '/r/management/profile-list' },
        {
          label: 'Users',
          key: 'users',
          children: [
            { label: 'Create User', route: '/r/management/user/create/' },
            { label: 'View', route: '/r/management/user-list/' },
          ],
        },
      ],
    },
    { type: 'header', title: 'Intervene' },
    {
      title: 'Intervene',
      icon: <ChatBubbleOutlineIcon />,
      key: 'intervene',
      permission: [globalpermissions?.pagePermissions?.interveneNew],
      route: '/r/intervene-dashboard',
    },
    { type: 'header', title: 'Web Form' },
    {
      title: 'Web Form',
      icon: <WebIcon />,
      key: 'webForm',
      permission: [globalpermissions?.pagePermissions?.webFormPageAccess],
      children: [
        { label: 'B2B2C Form', route: '/r/lead-web-form/' },
        { label: 'B2B Form', route: '/r/ops-verify/' },
      ],
    },
    { type: 'header', title: 'Templates' },
    {
      title: 'Whatsapp',
      icon: <WhatsAppIcon />,
      key: 'templates',
      permission: [globalpermissions?.pagePermissions?.templateDashboardNew],
      children: [
        {
          label: 'Templates',
          route: '/r/whatsapp/template-list',
          permission: [globalpermissions?.pagePermissions?.templateDashboardNew],
        },
        {
          label: 'Optin Users',
          route: '/r/whatsapp/optin-users',
          permission: [globalpermissions?.templateDashboard?.optinUsers],
        },
        {
          label: 'WA Settings',
          key: 'waSettings',
          permission: !!(
            globalpermissions?.templateDashboard?.metaEmbeddedSignup ||
            globalpermissions?.templateDashboard?.wabaAppList
          ),
          children: [
            // {
            //   label: 'Bot Authentication',
            //   route: '/r/setting/bot',
            // },
            {
              label: 'Meta Embedded Signup',
              route: '/r/setting/meta-embedded-signup',
              permission: [globalpermissions?.templateDashboard?.metaEmbeddedSignup],
            },
            {
              label: 'Waba App List',
              route: '/r/whatsapp/manager/app-list/',
              permission: [globalpermissions?.templateDashboard?.wabaAppList],
            },
          ],
        },
      ],
    },
    { type: 'header', title: 'Flow Builder' },
    {
      title: 'Flow Builder',
      icon: <AccountTreeIcon />,
      key: 'flowBuilder',
      permission: [globalpermissions?.pagePermissions?.flowBuilderAccess],
      route: '/r/flow/list',
    },
    {
      title: 'SMS',
      icon: <MessageIcon />,
      key: 'sms',
      permission: [globalpermissions?.pagePermissions?.smsDashboardAccess],
      children: [
        {
          label: 'SMS Templates',
          key: 'smsTemplates',
          route: '/r/sms/list',
          permission: [globalpermissions?.pagePermissions?.smsDashboardAccess],
        },
        {
          label: 'SMS Logs',
          key: 'smsLog',
          route: '/r/sms/logs',
          permission: [globalpermissions?.pagePermissions?.smsDashboardAccess],
        },
      ],
    },
    { type: 'header', title: 'E-Commerce' },
    {
      title: 'E-Commerce',
      icon: <StoreIcon />,
      key: 'ecommerce',
      permission: [globalpermissions?.pagePermissions?.ecommerceDashboard],
      children: [
        { label: 'Orders', route: '/r/e-commerce/orders' },
        { label: 'Product List', route: '/r/e-commerce/listing' },
        { label: 'E-commerce Flow', route: '/r/e-commerce/flow' },
      ],
    },
    { type: 'header', title: 'AI' },
    {
      title: 'AI',
      icon: <SmartToyIcon />,
      key: 'ai',
      permission: [globalpermissions?.pagePermissions?.aiAccess],
      route: '/r/ai',
    },
    { type: 'header', title: 'Vector Database' },
    {
      title: 'Vector Database',
      icon: <HubIcon />,
      key: 'vectorDatabase',
      permission: [globalpermissions?.pagePermissions?.vectorDatabaseAccess],
      route: '/r/vector/add-knowledge',
    },
    { type: 'header', title: 'Datasets' },
    {
      title: 'Datasets',
      icon: <MapIcon />,
      key: 'entity',
      permission: [globalpermissions?.pagePermissions?.selfServiceFormsAccess],
      route: '/r/datasets',
    },
    {
      type: 'header',
      title: 'Tests & Assessments'
    },
    { type: 'header', title: 'Tools' },
    {
      title: 'Tools',
      icon: <DataObjectIcon />,
      key: 'tools',
      permission: [globalpermissions?.pagePermissions?.urlGeneratorPageAccess],
      route: '/r/tools',
    },
    {
      title: 'Machadalo Studio',
      icon: <AutoAwesomeIcon />,
      key: 'machadaloStudio',
      permission: [globalpermissions?.machadaloStudioDashboard?.machadaloStudioAccess],
      route: '/r/machadalostudio/companyform',
    },
    //     { type: 'header', title: 'Settings' },
    //    {
    //   title: 'Settings',
    //   icon: <SettingsIcon />,
    //   key: 'settings',
    //   permission: [globalpermissions?.pagePermissions?.settingPageAccess],
    //   children: [
    //     {
    //       label: 'Bot Authentication',
    //       route: '/r/setting/bot',
    //     },
    //     {
    //       label: 'Meta Embedded Signup',
    //       route: '/r/setting/meta-embedded-signup',
    //     },
    //      {
    //       label: 'Waba App List',
    //       route: '/r/whatsapp/manager/app-list/',
    //     },
    //   ],
    // },
    // {
    //   title: 'Concept Builder',
    //   icon: <SchoolIcon />,
    //   key: 'conceptBuilder',
    //   permission: [globalpermissions?.performancePermissions?.conceptBuilder],
    //   children: subjectsLoading
    //     ? [{ label: 'Loading...', key: 'loading' }]
    //     : subjectMenuItems.length > 0
    //       ? subjectMenuItems
    //       : [{ label: 'No subjects available', key: 'empty' }],
    // },
    {
      title: 'Concept Builder',
      icon: <SchoolIcon />,
      key: 'conceptBuilder',
      permission: [globalpermissions?.performancePermissions?.conceptBuilder],
      ...((isSuperuser || actions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]))
        ? { route: '/r/student/concept-builder' }
        : {
          children: subjectsLoading
            ? [{ label: 'Loading...', key: 'loading' }]
            : subjectMenuItems.length > 0
              ? subjectMenuItems
              : [{ label: 'No subjects available', key: 'empty' }],
        }
      ),
    },
    {
      title: 'Practice Test',
      icon: <QuizIcon />,
      key: 'practiceTest',
      route: '/r/student/create-test',
      permission: [globalpermissions?.performancePermissions?.testCreator],
    },
    {
      title: 'Knowledge Map',
      icon: <AutoStoriesIcon />,
      key: 'knowledgeMap',
      route: '/r/student/knowledge-map',
      permission: [globalpermissions?.performancePermissions?.knowledgeMap],
    },
    {
      title: 'Performance Map',
      icon: <TimelineIcon />,
      key: 'performanceMap',
      route: '/r/student/performance-map',
      permission: [globalpermissions?.performancePermissions?.learningIntelligence],
    },
    {
      title: 'Parent Dashboard',
      icon: <FamilyRestroomIcon />,
      key: 'parentDashboard',
      route: '/r/dashboard/parent',
      permission: [globalpermissions?.performancePermissions?.parentDashboard],
    },
    {
      title: 'Teacher Dashboard',
      icon: <PersonPinIcon />,
      key: 'teacherDashboard',
      route: '/r/dashboard/teacher',
      permission: [globalpermissions?.performancePermissions?.teacherDashboard],
    },
    {
      title: 'Academic Info',
      icon: <InfoOutlinedIcon />,
      key: 'academicDashboard',
      route: '/r/student/notices',
      permission: [globalpermissions?.performancePermissions?.academicInfo],
    },
    {
      title: 'Question Bank',
      icon: <LocalLibraryIcon />,
      key: 'questionbank',
      route: '/r/student/question-bank',
      permission: [globalpermissions?.performancePermissions?.questionBank],
    },
  ];

  const menuSections = [
    {
      title: 'General',
      items: [
        menuConfig.find((m) => m.key === 'dashboard'),
        menuConfig.find((m) => m.key === 'campaignPlanning'),
        menuConfig.find((m) => m.key === 'campaignExecution'),
      ],
    },
    {
      title: 'Features',
      items: [
        menuConfig.find((m) => m.key === 'intervene'),
        menuConfig.find((m) => m.key === 'flowBuilder'),
        menuConfig.find((m) => m.key === 'templates'),
        menuConfig.find((m) => m.key === 'leads'),
        menuConfig.find((m) => m.key === 'sms'),
        menuConfig.find((m) => m.key === 'ecommerce'),
        menuConfig.find((m) => m.key === 'webForm'),
        menuConfig.find((m) => m.key === 'entity'),
        menuConfig.find((m) => m.key === 'conceptBuilder'),
        menuConfig.find((m) => m.key === 'practiceTest'),
        menuConfig.find((m) => m.key === 'knowledgeMap'),
        menuConfig.find((m) => m.key === 'performanceMap'),
        menuConfig.find((m) => m.key === 'parentDashboard'),
        menuConfig.find((m) => m.key === 'teacherDashboard'),
        menuConfig.find((m) => m.key === 'academicDashboard'),
        menuConfig.find((m) => m.key === 'questionbank'),
      ],
    },
    {
      title: 'Report',
      items: [menuConfig.find((m) => m.key === 'reporting')],
    },
    {
      title: 'Management',
      items: [menuConfig.find((m) => m.key === 'management')],
    },
    {
      title: 'Extra',
      items: [
        menuConfig.find((m) => m.key === 'ai'),
        menuConfig.find((m) => m.key === 'vectorDatabase'),
        menuConfig.find((m) => m.key === 'tools'),
        menuConfig.find((m) => m.key === 'settings'),
        menuConfig.find((m) => m.key === 'machadaloStudio'),
      ],
    },
  ];

  const renderNestedItems = (items, level = 0) => (
    <List component="div" disablePadding>
      {items.map((item) => {
        if (!item) return null;
        const hasChildren = item.children && item.children.length > 0;
        const expanded = expandMap[item.key];
        if (!item.permission || actions.userHasPermission(item.permission)) {
          return (
            <React.Fragment key={item.label || item.title}>
              <ListItem
                button
                onClick={() => (hasChildren ? toggleExpand(item.key) : handleNavigate(item.route))}
                sx={{
                  pl: level === 0 ? 3 : 2 + level * 1,
                  color:
                    isActive(item.route) || isAnyChildRouteActive(item.children)
                      ? 'primary.main'
                      : '#1e293b',
                  backgroundColor:
                    isActive(item.route) || isAnyChildRouteActive(item.children)
                      ? '#e0f2fe'
                      : 'transparent',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e5e7eb',
                  },
                }}
              >
                {drawerOpen && <ListItemText primary={item.label || item.title} />}
                {!drawerOpen && (
                  <Tooltip title={item.label || item.title} placement="right">
                    <ListItemIcon />
                  </Tooltip>
                )}
                {hasChildren && (expanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
              {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  {renderNestedItems(item.children, level + 1)}
                </Collapse>
              )}
            </React.Fragment>
          );
        }
        return null;
      })}
    </List>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {isSmallScreen && (
        <IconButton
          onClick={handleToggle}
          sx={{
            position: 'fixed',
            top: { xs: '75px', sm: '28px' },
            left: { xs: '28px', sm: '28px' },
            // zIndex: theme.zIndex.drawer + 1,
            zIndex: 111,
            backgroundColor: 'white',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      )}

      <Drawer
        variant={isSmallScreen ? 'temporary' : 'permanent'}
        open={isSmallScreen ? drawerOpen : drawerOpen}
        onClose={handleToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerOpen ? drawerWidth : isSmallScreen ? 0 : 60,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : isSmallScreen ? 0 : 60,
            height: '100%',
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            backgroundColor: '#f9fafb',
            color: '#1e293b',
            transform: isSmallScreen && !drawerOpen ? 'translateX(-100%)' : 'translateX(0)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Logo Section */}
          {(!isSmallScreen || drawerOpen) && (
            <Box
              sx={{
                flexShrink: 0,
                p: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: drawerOpen ? '80px' : '60px',
              }}
            >
              {drawerOpen ? (
                <img
                  src={logo || Logo}
                  alt="Logo"
                  style={{
                    cursor: 'pointer',
                    maxWidth: '100%',
                    maxHeight: '60px',
                    objectFit: 'contain',
                  }}
                  onClick={() => handleNavigate(actions.getDynamicHomePageLink())}
                />
              ) : (
                <img
                  src={logo || Logo}
                  alt="Logo"
                  style={{
                    cursor: 'pointer',
                    width: '40px',
                    height: '40px',
                    maxWidth: 'fit-content',
                    objectFit: 'contain',
                  }}
                  onClick={() => handleNavigate(actions.getDynamicHomePageLink())}
                />
              )}
            </Box>
          )}

          {(!isSmallScreen || drawerOpen) && <Divider />}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: drawerOpen ? 1 : 0 }}>
            {drawerOpen && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                  pr: 1,
                  pl: 2.6,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    fontSize: { xs: "12px", sm: "14px" },
                  }}
                >
                  Powered by Machadalo
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: "italic",
                    color: "#777",
                    fontSize: { xs: "11px", sm: "13px" },
                  }}
                >
                  Brand owned by Vijaikirti Technologies Pvt. Ltd
                </Typography>
              </Box>
            )}
            {/* Header with toggle button */}
            {(!isSmallScreen || drawerOpen) && (
              <Box
                sx={{
                  flexShrink: 0,
                  p: 1,
                  display: 'flex',
                  justifyContent: drawerOpen ? 'flex-end' : 'center',
                  alignItems: 'center',
                  height: 'auto',
                }}
              >
                <IconButton onClick={handleToggle}>
                  {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                </IconButton>
              </Box>
            )}
          </Box>


          {(!isSmallScreen || drawerOpen) && <Divider />}

          {/* Menu Items */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              pt: isSmallScreen ? 8 : 0,
            }}
          >
            {menuSections
              .filter((section) => hasVisibleSection(section))
              .map((section) => (
                <Box key={section.title} sx={{ px: 1 }}>
                  {drawerOpen && (
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        px: { xs: 0, md: 2 },
                        pt: { xs: 0, md: 2 },
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {section.title}
                    </Typography>
                  )}
                  {section.items.map((sectionItem) => {
                    if (!sectionItem) return null;

                    const hasPermission =
                      !sectionItem.permission ||
                      (Array.isArray(sectionItem.permission) &&
                        actions.userHasPermission(sectionItem.permission));

                    if (hasPermission) {
                      return (
                        <Box key={sectionItem.key} sx={{ alignItems: 'center' }}>
                          <Tooltip
                            title={!drawerOpen ? sectionItem.title : ''}
                            placement="right"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  pointerEvents: 'auto',
                                },
                              },
                            }}
                          >
                            <ListItem
                              ref={(el) => (itemRefs.current[sectionItem.key] = el)}
                              button
                              onClick={() => {
                                if (!drawerOpen) {
                                  setDrawerOpen(true);

                                  setTimeout(() => {
                                    itemRefs.current[sectionItem.key]?.scrollIntoView({
                                      behavior: "smooth",
                                      block: "center",
                                    });
                                  }, 200);
                                }

                                if (sectionItem.route) {
                                  handleNavigate(sectionItem.route);
                                } else {
                                  toggleExpand(sectionItem.key);
                                }
                              }}
                              sx={{
                                color: '#1e293b',
                                transition: 'background-color 0.3s',
                                '&:hover': { backgroundColor: '#e5e7eb' },
                                borderRadius: 1,
                                // mx: 1,
                                my: 0.25,
                                px: drawerOpen ? { xs: 0, md: 2 } : 0,
                                width: drawerOpen ? 'auto' : 48,
                                mx: drawerOpen ? 0 : 'auto',
                                justifyContent: drawerOpen ? 'flex-start' : 'center',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  color:
                                    isActive(sectionItem.route) ||
                                      isAnyChildRouteActive(sectionItem.children)
                                      ? 'primary.main'
                                      : 'inherit',
                                  minWidth: 0,
                                  width: 40,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                }}
                              >
                                {sectionItem.icon}
                              </ListItemIcon>

                              {drawerOpen && (
                                <ListItemText primary={sectionItem.title} sx={{ ml: 0 }} />
                              )}
                              {drawerOpen &&
                                sectionItem.children &&
                                (expandMap[sectionItem.key] ? <ExpandLess /> : <ExpandMore />)}
                            </ListItem>
                          </Tooltip>
                          {sectionItem.children && (
                            <Collapse in={expandMap[sectionItem.key]} timeout="auto" unmountOnExit>
                              {renderNestedItems(sectionItem.children)}
                            </Collapse>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Box>
              ))}
          </Box>

          {/* Profile Section at Bottom */}
          <Box sx={{ flexShrink: 0 }}>
            <Divider />
            <Box
              sx={{
                p: drawerOpen ? 2 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: drawerOpen ? 'space-between' : 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#e5e7eb',
                },
              }}
              onClick={handleMenuOpen}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: '#6071fb',
                    color: 'white',
                    width: drawerOpen ? 40 : 32,
                    height: drawerOpen ? 40 : 32,
                  }}
                >
                  {userInfo?.first_name
                    ? `${userInfo.first_name[0].toUpperCase()}${userInfo.last_name ? userInfo.last_name[0].toUpperCase() : ''}`
                    : 'G'}
                </Avatar>
                {drawerOpen && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {userInfo?.first_name || 'Guest'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {userInfo?.email || 'guest@example.com'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {settingDrop
          .filter((item) => item?.isVisible)
          .map((item, index) =>
            item.label === 'Logout' ? (
              <MenuItem
                key={index}
                onClick={() => handleMenuItemClick(item.value)}
                sx={{
                  color: '#d32f2f',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#d32f2f' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            ) : (
              <MenuItem key={index} onClick={() => handleMenuItemClick(item.value)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            )
          )}
      </Menu>
    </Box>
  );
};

export default ModernSidebar;